import type { ListQuery, Page } from "@estelle/domain";

/**
 * Pages exhaustively through a repository's `list` method.
 *
 * The fixture adapter caps `limit` at 200 (`MAX_LIMIT` in
 * `adapters/fixture/query.ts`) and this dataset carries 300 orders — one
 * call cannot return the whole set no matter what `limit` asks for. A report
 * has to see every matching row to total correctly, so this loops on
 * `nextCursor` until the adapter says there is nothing left.
 *
 * A real reports API would aggregate this server-side rather than have a
 * report tab pull every row across the wire to sum in the browser. This is
 * the client-side stand-in that makes the fixture-backed screens correct
 * today; it is not the pattern to carry into the real adapter.
 */
export async function fetchAllPages<T>(
  list: (query: ListQuery) => Promise<Page<T>>,
  query: Omit<ListQuery, "cursor"> = {},
): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | undefined;

  for (;;) {
    const page = await list({ ...query, limit: query.limit ?? 200, cursor });
    items.push(...page.items);
    if (!page.nextCursor) break;
    cursor = page.nextCursor;
  }

  return items;
}
